import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Plus } from 'lucide-react';
import {
  deleteKnowledgeBase,
  getGoogleDriveIngestInfo,
  listKnowledgeBases,
  triggerKnowledgeSync,
} from '../../api/knowledgeBases';
import EmptyState from '../../components/ui/EmptyState';
import { EMPTY_STATE_ICONS } from '../../support/iconMappings';
import { getApiErrorMessage } from '../../utils/errors';
import GoogleDriveConnectPanel from './components/GoogleDriveConnectPanel';
import KnowledgeBasesMobileList from './components/KnowledgeBasesMobileList';
import KnowledgeBasesTable from './components/KnowledgeBasesTable';

const PER_PAGE = 20;

function KnowledgeBasesList() {
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ page: 1, per_page: PER_PAGE, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [driveNotice, setDriveNotice] = useState('');
  const [googleDriveInfo, setGoogleDriveInfo] = useState(null);
  const [syncMessage, setSyncMessage] = useState('');
  const [syncError, setSyncError] = useState('');
  const [syncingIds, setSyncingIds] = useState({});
  const [deletingId, setDeletingId] = useState('');
  const refreshTimeoutsRef = useRef([]);

  const clearRefreshTimeouts = useCallback(() => {
    refreshTimeoutsRef.current.forEach(clearTimeout);
    refreshTimeoutsRef.current = [];
  }, []);

  const loadItems = useCallback(async (page = 1, { silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setError('');
    }

    try {
      const response = await listKnowledgeBases({ page, perPage: PER_PAGE });
      setItems(response.data || []);
      setMeta(response.meta || { page, per_page: PER_PAGE, total: 0 });
    } catch (err) {
      if (!silent) {
        setError(getApiErrorMessage(err, 'Não foi possível carregar as bases.'));
        setItems([]);
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  const scheduleStatusRefresh = useCallback(
    (page) => {
      clearRefreshTimeouts();
      [3000, 8000].forEach((delay) => {
        const timeoutId = setTimeout(() => {
          loadItems(page, { silent: true });
        }, delay);
        refreshTimeoutsRef.current.push(timeoutId);
      });
    },
    [clearRefreshTimeouts, loadItems]
  );

  useEffect(() => {
    loadItems(1);
    return clearRefreshTimeouts;
  }, [loadItems, clearRefreshTimeouts]);

  useEffect(() => {
    getGoogleDriveIngestInfo()
      .then(setGoogleDriveInfo)
      .catch(() => setGoogleDriveInfo(null));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('google_drive') === 'connected') {
      setDriveNotice('Google Drive conectado com sucesso.');
    } else if (params.get('google_drive') === 'error') {
      setError(params.get('message') || 'Falha ao conectar Google Drive.');
    }
  }, [location.search]);

  const handleSync = async (kb) => {
    const previousStatus = kb.status;
    const checksSource = kb.source_type === 's3' || kb.source_type === 'google_drive';
    setSyncMessage('');
    setSyncError('');
    setSyncingIds((current) => ({ ...current, [kb.id]: true }));
    setItems((current) =>
      current.map((item) =>
        item.id === kb.id ? { ...item, status: 'syncing' } : item
      )
    );

    try {
      await triggerKnowledgeSync(kb.id);
      setSyncMessage(
        checksSource
          ? `Verificando atualizações na fonte e sincronizando "${kb.name}"...`
          : `Sincronização iniciada para "${kb.name}".`
      );
      scheduleStatusRefresh(meta.page);
    } catch (err) {
      setSyncError(getApiErrorMessage(err, 'Não foi possível iniciar a sincronização.'));
      setItems((current) =>
        current.map((item) =>
          item.id === kb.id ? { ...item, status: previousStatus } : item
        )
      );
    } finally {
      setSyncingIds((current) => {
        const next = { ...current };
        delete next[kb.id];
        return next;
      });
    }
  };

  const handleDelete = async (kb) => {
    if (!window.confirm(
      `Excluir permanentemente a base "${kb.name}"? Ela será removida dos agentes vinculados.`
    )) {
      return;
    }

    setDeletingId(kb.id);
    setSyncMessage('');
    setSyncError('');
    try {
      await deleteKnowledgeBase(kb.id);
      const targetPage = items.length === 1 && meta.page > 1 ? meta.page - 1 : meta.page;
      setSyncMessage(`Base "${kb.name}" excluída com sucesso.`);
      await loadItems(targetPage, { silent: true });
    } catch (err) {
      setSyncError(getApiErrorMessage(err, 'Não foi possível excluir a base.'));
    } finally {
      setDeletingId('');
    }
  };

  const infoNotice = useMemo(() => {
    if (error) {
      return { type: 'error', message: error, retry: true };
    }
    if (syncError) {
      return { type: 'error', message: syncError };
    }
    if (syncMessage) {
      return { type: 'info', message: syncMessage };
    }
    if (driveNotice) {
      return { type: 'success', message: driveNotice };
    }
    return null;
  }, [error, syncError, syncMessage, driveNotice]);

  const totalPages = Math.max(1, Math.ceil(meta.total / meta.per_page));
  const hasPagination = meta.total > meta.per_page;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-content-primary">Bases de conhecimento</h1>
          <p className="text-content-muted text-sm mt-1">
            Bases de conhecimento vinculadas aos seus agentes.
          </p>
        </div>
        <Link to="/knowledge-bases/new" className="btn-primary text-sm px-4 py-2.5 shrink-0 inline-flex items-center gap-1.5">
          <Plus className="h-4 w-4 shrink-0" aria-hidden />
          Nova base
        </Link>
      </div>

      <GoogleDriveConnectPanel
        refreshKey={location.search}
        oauthConfigured={googleDriveInfo?.oauth_configured === true}
        instructions={googleDriveInfo?.instructions}
      />

      {infoNotice && (
        <div
          className={`rounded-xl border px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${
            infoNotice.type === 'error'
              ? 'border-red-200 bg-red-50'
              : infoNotice.type === 'success'
                ? 'border-emerald-200 bg-emerald-50'
                : 'border-forge-200 bg-forge-50'
          }`}
        >
          <p
            className={`text-sm ${
              infoNotice.type === 'error'
                ? 'text-red-700'
                : infoNotice.type === 'success'
                  ? 'text-emerald-700'
                  : 'text-forge-700'
            }`}
          >
            {infoNotice.message}
          </p>
          {infoNotice.retry && (
            <button type="button" onClick={() => loadItems(meta.page)} className="btn-secondary text-sm px-3 py-2">
              Tentar novamente
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="surface-card px-4 py-12 text-center">
          <p className="text-content-muted text-sm">Carregando bases...</p>
        </div>
      ) : items.length === 0 && !error ? (
        <EmptyState
          icon={EMPTY_STATE_ICONS.knowledgeBases}
          title="Nenhuma base cadastrada"
          description="Importe uma pasta do Google Drive ou use link direto / S3."
          action={(
            <Link to="/knowledge-bases/new" className="btn-primary text-sm px-4 py-2.5 inline-flex items-center gap-1.5">
              <Plus className="h-4 w-4 shrink-0" aria-hidden />
              Criar primeira base
            </Link>
          )}
        />
      ) : (
        <>
          <KnowledgeBasesTable
            items={items}
            syncingIds={syncingIds}
            deletingId={deletingId}
            onSync={handleSync}
            onDelete={handleDelete}
          />
          <KnowledgeBasesMobileList
            items={items}
            syncingIds={syncingIds}
            deletingId={deletingId}
            onSync={handleSync}
            onDelete={handleDelete}
          />

          {hasPagination && (
            <div className="flex items-center justify-between gap-3 pt-2">
              <p className="text-xs text-content-subtle">
                {meta.total} base{meta.total !== 1 ? 's' : ''} · página {meta.page} de {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={meta.page <= 1 || loading}
                  onClick={() => loadItems(meta.page - 1)}
                  className="btn-secondary text-xs px-3 py-2 disabled:opacity-40"
                >
                  Anterior
                </button>
                <button
                  type="button"
                  disabled={meta.page >= totalPages || loading}
                  onClick={() => loadItems(meta.page + 1)}
                  className="btn-secondary text-xs px-3 py-2 disabled:opacity-40"
                >
                  Próxima
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default KnowledgeBasesList;
